import { Injectable, OnDestroy } from '@angular/core';
import { defer, EMPTY, forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, finalize, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { GenderType, SoftwareOptions } from '@shared/models/enums/enums';
import { SoftwareOptionsService } from '@shared/services/software-options.service';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { SpinnerService } from '@core/services/spinner.service';
import { LoggerService } from '@core/services/logger/logger.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { PatientStore } from '@modules/patient/state/patient-store';
import { PatientQuery } from '@modules/patient/state/patient-query';
import { UnifiedPatientsApiService } from './unified-patients-api.service';
import { PatientFormsMachineService } from '@modules/unified-patients/services/patient-forms-machine.service';
import { PatientFormsServicesToken } from '@modules/unified-patients/services/patient-forms-services.token';
import { mapRxPatientDataToPatient } from '@modules/unified-patients/services/patient-mapping';
import { PatientLinkApiService } from '@modules/unified-patients/services/patient-link-api.service';
import { emptyGuid } from '@shared/models/consts';

/** Patient type for iTero-native patients (expected by Rx-MS) */
const PATIENT_TYPE_ITERO = 1;

@Injectable({ providedIn: 'root' })
export class UnifiedPatientsIntegrationService implements OnDestroy {
	private readonly stateSubscription: Subscription | null = null;
	private readonly readOnlySubscription: Subscription | null = null;
	private isEnabledSubject$: Observable<boolean> | null = null;

	get isEnabled$(): Observable<boolean> {
		this.isEnabledSubject$ ??= forkJoin([
			this.softwareOptionsService.hasCompanySoftwareOption$(SoftwareOptions.NewFlowRx).pipe(take(1)),
			this.softwareOptionsService.hasCompanySoftwareOption$(SoftwareOptions.UnifiedPatientsEnabled).pipe(take(1))
		]).pipe(
			switchMap(([hasNewFlowRx, hasUnifiedPatientsEnabled]) => {
				if (hasNewFlowRx || !hasUnifiedPatientsEnabled || this.hostPlatformService.isOfflineMode()) {
					return of(false);
				}

				return this.shellQuery.companyId$.pipe(
					filter((id): id is number => id != null && id > 0),
					take(1),
					switchMap(companyId =>
						this.unifiedPatientsApiService.initialize(companyId).pipe(
							tap(res => {
								if (res.ClinicianId) {
									this.patientLinkApiService.clinicianId = res.ClinicianId;
								}
							}),
							map(res => res.Enabled),
							catchError(() => of(false))
						)
					)
				);
			}),
			shareReplay({ bufferSize: 1, refCount: false })
		);

		return this.isEnabledSubject$;
	}

	get isPatientValid(): boolean {
		return this.machine.state.matches('patientCard.committed.yes');
	}

	constructor(
		private readonly softwareOptionsService: SoftwareOptionsService,
		private readonly hostPlatformService: HostPlatformService,
		private readonly spinnerService: SpinnerService,
		private readonly shellQuery: ShellQuery,
		private readonly patientStore: PatientStore,
		private readonly patientQuery: PatientQuery,
		private readonly unifiedPatientsApiService: UnifiedPatientsApiService,
		private readonly machine: PatientFormsMachineService,
		private readonly machineServices: PatientFormsServicesToken,
		private readonly patientLinkApiService: PatientLinkApiService,
		private readonly logger: LoggerService
	) {
		this.stateSubscription = this.machine.state$.subscribe(state => {
			const isCommitted = state.matches('patientCard.committed.yes');
			const patient = state.context.iTeroPatient;

			if (isCommitted && patient) {
				this.patientStore.updatePatient(
					{
						UID: patient.id ?? '',
						FirstName: patient.firstName,
						LastName: patient.lastName,
						DateOfBirth: patient.dateOfBirth ? new Date(patient.dateOfBirth) : '',
						Gender: this.mapGenderToNumber(patient.gender),
						ChartNumber: patient.chartNumber ?? '',
						ZipCode: '',
						AlignPatientId: '',
						Type: PATIENT_TYPE_ITERO
					},
					null,
					null
				);
			} else if (!patient) {
				this.patientStore.update({ patient: null });
			}
		});

		// Make Invisalign toggle ON by default
		this.machine.setDefaultToggle(true);

		// Sync read-only state from host into the machine
		this.readOnlySubscription = this.shellQuery.getReadOnlyState('patient').subscribe(isReadOnly => {
			this.machine.setReadOnly(isReadOnly);
		});
	}

	ngOnDestroy(): void {
		this.stateSubscription?.unsubscribe();
		this.readOnlySubscription?.unsubscribe();
	}

	reset(): void {
		this.isEnabledSubject$ = null;
		this.machine.clear();
	}

	/** Call from component ngOnInit. Loads existing patient link status when patientGuid becomes available. */
	loadExistingUnifiedPatientHandler$(): Observable<void> {
		return defer(() => {
			this.spinnerService.start();
			this.setPreviewFromStore();

			return this.isEnabled$;
		}).pipe(
			take(1),
			switchMap(enabled => {
				if (!enabled || (!this.shellQuery.rxId && !this.shellQuery.clonedFromRxID && !this.shellQuery.patientGuidProvided)) {
					return EMPTY;
				}

				return this.patientQuery.patientGuid$.pipe(
					filter((guid): guid is string => guid != null && guid !== emptyGuid),
					take(1),
					switchMap(patientGuid =>
						this.machineServices.loadExistingPatient(patientGuid, this.patientQuery.patient$).pipe(
							tap(patient => {
								if (patient) {
									this.machine.setExistingPatient(patient);
								}
							}),
							map(() => {})
						)
					)
				);
			}),
			finalize(() => {
				this.spinnerService.stop();
				this.machine.markReady();
			})
		);
	}

	savePatient(): Observable<boolean> {
		if (!this.isPatientValid) {
			return of(false);
		}

		const machineState = this.machine.state;

		return this.machineServices.savePatient(machineState.context, machineState.matches.bind(machineState)).pipe(
			tap(result => {
				if (result.savedIteroPatient) {
					const s = result.savedIteroPatient;

					this.updateStore({
						uid: s.uid,
						firstName: s.firstName,
						lastName: s.lastName,
						dateOfBirth: s.dateOfBirth,
						gender: s.gender,
						chartNumber: s.chartNumber,
						alignPatientId: ''
					});
				}

				if (result.linkedPatient && machineState.context.iTeroPatient) {
					const p = machineState.context.iTeroPatient;

					this.updateStore({
						uid: result.linkedPatient.iteroPatientId,
						firstName: p.firstName,
						lastName: p.lastName,
						dateOfBirth: p.dateOfBirth,
						gender: this.mapGenderToNumber(p.gender),
						chartNumber: p.chartNumber,
						alignPatientId: result.linkedPatient.invisalignPatientId
					});
				}
			}),
			map(result => {
				if (!result.success) {
					this.logger.error('savePatient failed', { module: 'UnifiedPatientsIntegrationService' });
				} else {
					this.machine.markRxSaved();
				}

				return result.success;
			})
		);
	}

	private updateStore(patient: {
		uid: string;
		firstName: string;
		lastName: string;
		dateOfBirth: string;
		gender: number | undefined;
		chartNumber: string;
		alignPatientId: string;
	}): void {
		const currentPatient = this.patientQuery.patient;

		this.patientStore.updatePatient(
			{
				UID: patient.uid,
				FirstName: patient.firstName ?? currentPatient?.FirstName ?? '',
				LastName: patient.lastName ?? currentPatient?.LastName ?? '',
				DateOfBirth: patient.dateOfBirth ? new Date(patient.dateOfBirth) : currentPatient?.DateOfBirth ?? '',
				Gender: patient.gender ?? currentPatient?.Gender,
				ChartNumber: patient.chartNumber ?? currentPatient?.ChartNumber ?? '',
				ZipCode: currentPatient?.ZipCode ?? '',
				AlignPatientId: patient.alignPatientId ?? currentPatient?.AlignPatientId ?? '',
				Type: PATIENT_TYPE_ITERO
			},
			null,
			null
		);
	}

	private mapGenderToNumber(gender: string): number | undefined {
		switch (gender?.toLowerCase()) {
			case 'male':
				return GenderType.Male;
			case 'female':
				return GenderType.Female;
			default:
				return undefined;
		}
	}

	private setPreviewFromStore(): void {
		const p = this.patientQuery.patient;

		if (!p) {
			return;
		}

		this.machine.previewPatient = mapRxPatientDataToPatient({
			UID: p.UID,
			FirstName: p.FirstName,
			LastName: p.LastName,
			DateOfBirth: p.DateOfBirth,
			Gender: p.Gender,
			ChartNumber: p.ChartNumber
		});
	}
}
